import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw, Sun, Moon, LogOut, User, Smartphone, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useAvatars } from "@/context/AvatarContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationsBell } from "@/components/common/NotificationsBell";
import { PendingTasksDot } from "@/components/common/AssignedTasksNotifications";
import { AddToDeviceDialog } from "@/components/common/AddToDeviceDialog";
import { GlobalSearchDialog } from "@/components/common/GlobalSearchDialog";
import { MobileSidebar } from "./Sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Topbar() {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { avatarFor } = useAvatars();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Shortcut ⌘K / Ctrl+K per aprire la ricerca globale ovunque.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleRefresh = () => {
    setSpinning(true);
    setTimeout(() => {
      setSpinning(false);
      toast.success("Dati aggiornati", { description: "Sincronizzazione completata (mock)." });
    }, 700);
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl sm:px-6">
      <MobileSidebar />
      <Button
        data-testid="global-back-button"
        variant="ghost"
        size="icon"
        onClick={() => navigate(-1)}
        className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
        title="Torna alla sezione precedente"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <button
        data-testid="global-search-trigger"
        onClick={() => setOpen(true)}
        className="flex h-9 min-w-0 flex-1 max-w-md items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left">
          <span className="hidden sm:inline">Cerca in tutta la piattaforma</span>
          <span className="sm:hidden">Cerca…</span>
        </span>
        <kbd className="hidden rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] sm:inline">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <div className="relative">
          <Button
            data-testid="refresh-button"
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={spinning ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          </Button>
          <PendingTasksDot />
        </div>

        <NotificationsBell />

        <Button
          data-testid="add-to-device-trigger"
          variant="ghost"
          size="icon"
          onClick={() => setAddOpen(true)}
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
          title="Aggiungi accesso rapido"
        >
          <Smartphone className="h-4 w-4" />
        </Button>

        <Button
          data-testid="theme-toggle"
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="ml-1 flex items-center gap-2 rounded-full outline-none">
              <Avatar className="h-8 w-8 border border-border/60">
                <AvatarImage src={avatarFor(user?.id, user?.avatar)} alt={user?.name} />
                <AvatarFallback className="text-xs">{user?.initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="text-sm font-medium text-foreground">{user?.name}</span>
              <span className="text-xs font-normal text-muted-foreground">{user?.email || "Da configurare"}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/impostazioni")}>
              <User className="mr-2 h-4 w-4" /> Profilo
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-logout"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="text-red-500 focus:text-red-500"
            >
              <LogOut className="mr-2 h-4 w-4" /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <GlobalSearchDialog open={open} onOpenChange={setOpen} />
      <AddToDeviceDialog open={addOpen} onOpenChange={setAddOpen} />
    </header>
  );
}
